import {ValidationError} from './independentAgent';
import {Async, Empty, isLoaded, Loaded} from 'Core/models/Async';
import {Email, UnvalidatedEmail, ValidEmail} from 'Core/models/Email';
import {EmptyString} from 'Core/models/EmptyString';
import {Page} from 'Core/models/Page';
import {EmptyPhoneNumber, PhoneNumber, ValidPhoneNumber} from 'Core/models/PhoneNumber';
import {ValidatableNonEmptyString} from 'Core/models/ValidatableNonEmptyString';
import {Either, left} from 'fp-ts/lib/Either';
import {constant} from 'fp-ts/lib/function';
import {fold as foldOption, fromNullable} from 'fp-ts/lib/Option';
import {pipe} from 'fp-ts/lib/pipeable';
import {Agent} from 'http';
import {Lens} from 'monocle-ts';
import {createAction} from 'Shared/utilities/actionCreator';
import {PageFetchAction} from 'Shared/utilities/sagaApiGenerators';
import {AgentMapping, AgentMappingValue, UnvalidatedAgentMapping, ValidAgentMapping} from 'Users/models/AgentMapping';
import {EmptyUserFile, UserFile} from 'Users/models/UserFile';
import {UserModel} from 'Users/models/UserModel';

export enum AgentAdministrationActionType {
  IMPERSONATE_AGENT = 'agentAdministration/IMPERSONATE_AGENT',
  STOP_IMPERSONATION = 'agentAdministration/STOP_IMPERSONATION ',
  FETCH_AGENTS = 'agentAdministration/FETCH_AGENTS',
  GET_AGENT = 'agentAdministration/GET_AGENT',
  CHANGE_AGENTS = 'agentAdministration/CHANGE_AGENTS',
  OFFBOARD_AGENT = 'agentAdministration/OFFBOARD_AGENT',
  CHANGE_AGENT_MAPPING = 'agentAdministration/CHANGE_AGENT_MAPPING',
  LOAD_AGENT_MAPPING = 'agentAdministration/LOAD_AGENT_MAPPING',
  CREATE_AGENT_MAPPING = 'agentAdministration/CREATE_AGENT_MAPPING',
  CLEAR_AGENT_MAPPING = 'agentAdministration/CLEAR_AGENT_MAPPING',
  VALIDATE_EMAIL = 'agentAdministration/VALIDATE_EMAIL',
  SET_EMAIL = 'agentAdministration/SET_EMAIL',
  VALIDATE_PHONE = 'agentAdministration/VALIDATE_PHONE',
  SET_PHONE = 'agentAdministration/SET_PHONE',
  VALIDATE_USERNAME = 'agentAdministration/VALIDATE_USERNAME',
  SET_USERNAME = 'agentAdministration/SET_USERNAME',
  CLEAR = 'agentAdministration/CLEAR',
  CHANGE_ERRORS = 'agentAdministration/ CHANGE_ERRORS',
}

export const clear = () => createAction(AgentAdministrationActionType.CLEAR);
export const validateUsername = (username: string) =>
  createAction(AgentAdministrationActionType.VALIDATE_USERNAME, {username});
export const setUsername = (username: ValidatableNonEmptyString) =>
  createAction(AgentAdministrationActionType.SET_USERNAME, {username});
export const validatePhone = (phone: string) => createAction(AgentAdministrationActionType.VALIDATE_PHONE, {phone});
export const setPhone = (phone: Async<Either<PhoneNumber, ValidPhoneNumber>>) =>
  createAction(AgentAdministrationActionType.SET_PHONE, {phone});
export const validateEmail = (email: string) => createAction(AgentAdministrationActionType.VALIDATE_EMAIL, {email});
export const setEmail = (email: Async<Either<Email, ValidEmail>>) =>
  createAction(AgentAdministrationActionType.SET_EMAIL, {email});
export const stopImpersonation = (username: string) =>
  createAction(AgentAdministrationActionType.STOP_IMPERSONATION, {username});
export const impersonateAgent = (username: string, impersonatedUserName: string) =>
  createAction(AgentAdministrationActionType.IMPERSONATE_AGENT, {
    username,
    impersonatedUserName,
  });
export const createAgentMapping = (agentMapping: ValidAgentMapping, isSupport: boolean, id?: string) =>
  createAction(AgentAdministrationActionType.CREATE_AGENT_MAPPING, {
    agentMapping,
    isSupport,
    id,
  });
export const fetchAgents = (pageOption?: Page<Agent>) => {
  const payload = pipe(
    fromNullable(pageOption),
    foldOption(constant(PageFetchAction.Initial({filter: {}})), page =>
      PageFetchAction.Additional({existingPage: page, filter: {}})
    )
  );

  return createAction(AgentAdministrationActionType.FETCH_AGENTS, payload);
};
export const getAgent = (agentId: string, encryptedIdentifier?: string) =>
  createAction(AgentAdministrationActionType.GET_AGENT, {agentId, encryptedIdentifier});
export const offboardAgent = (agentId: number, changeAgent: (agent: Async<UserModel>) => any) =>
  createAction(AgentAdministrationActionType.OFFBOARD_AGENT, {
    agentId,
    changeAgent,
  });
export const changeAgents = (agents: Async<Page<UserModel>>) =>
  createAction(AgentAdministrationActionType.CHANGE_AGENTS, agents);
export const loadAgentMapping = (agentMapping: Async<AgentMapping>) => {
  return createAction(AgentAdministrationActionType.LOAD_AGENT_MAPPING, agentMapping);
};
export const changeAgentMapping = (agentMapping: Async<AgentMapping>) => {
  const validatedAgentMapping =
    agentMapping.key === 'Loaded'
      ? (new Loaded(ValidAgentMapping.create(agentMapping.Data.value)) as Async<AgentMapping>)
      : agentMapping;

  return createAction(AgentAdministrationActionType.CHANGE_AGENT_MAPPING, validatedAgentMapping);
};
export const clearAgentMapping = () => createAction(AgentAdministrationActionType.CLEAR_AGENT_MAPPING);

export const changeAgentErrors = (errors: ValidationError[]) =>
  createAction(AgentAdministrationActionType.CHANGE_ERRORS, errors);

type AgentAdministrationAction =
  | ReturnType<typeof changeAgents>
  | ReturnType<typeof changeAgentMapping>
  | ReturnType<typeof setEmail>
  | ReturnType<typeof clear>
  | ReturnType<typeof setPhone>
  | ReturnType<typeof setUsername>
  | ReturnType<typeof loadAgentMapping>
  | ReturnType<typeof clearAgentMapping>
  | ReturnType<typeof changeAgentErrors>;

export const DEFAULT_STATE: AgentAdministrationState = {
  agentMapping: new Loaded(
    new UnvalidatedAgentMapping({
      agencyAssociations: [],
      email: new Loaded(left(new UnvalidatedEmail(''))),
      firstName: new EmptyString(),
      lastName: new EmptyString(),
      phoneNumber: new Loaded(left(new EmptyPhoneNumber())),
      userName: new Loaded(left(new EmptyString())),
    })
  ),
  agents: new Empty(),
  file: new EmptyUserFile(),
  originalAgentMapping: new Empty(),
  validationErrors: [],
};

type AgentAdministrationState = {
  agents: Async<Page<UserModel>>;
  file: UserFile;
  agentMapping: Async<AgentMapping>;
  originalAgentMapping: Async<AgentMapping>;
  validationErrors: ValidationError[];
};

export default (
  state: AgentAdministrationState = DEFAULT_STATE,
  action: AgentAdministrationAction
): AgentAdministrationState => {
  switch (action.type) {
    case AgentAdministrationActionType.SET_USERNAME: {
      const usernameLens = Lens.fromProp<AgentMappingValue>()('userName');
      return isLoaded(state.agentMapping)
        ? {
            ...state,
            agentMapping: new Loaded(
              ValidAgentMapping.create(usernameLens.set(action.payload.username)(state.agentMapping.Data.value))
            ),
          }
        : state;
    }
    case AgentAdministrationActionType.SET_PHONE: {
      const phoneLens = Lens.fromProp<AgentMappingValue>()('phoneNumber');
      return isLoaded(state.agentMapping)
        ? {
            ...state,
            agentMapping: new Loaded(
              ValidAgentMapping.create(phoneLens.set(action.payload.phone)(state.agentMapping.Data.value))
            ),
          }
        : state;
    }
    case AgentAdministrationActionType.SET_EMAIL: {
      const emailLens = Lens.fromProp<AgentMappingValue>()('email');
      return isLoaded(state.agentMapping)
        ? {
            ...state,
            agentMapping: new Loaded(
              ValidAgentMapping.create(emailLens.set(action.payload.email)(state.agentMapping.Data.value))
            ),
          }
        : state;
    }
    case AgentAdministrationActionType.LOAD_AGENT_MAPPING:
      return {
        ...state,
        agentMapping: action.payload,
        originalAgentMapping: action.payload,
      };
    case AgentAdministrationActionType.CHANGE_AGENT_MAPPING:
      return {
        ...state,
        agentMapping: action.payload,
      };
    case AgentAdministrationActionType.CHANGE_AGENTS:
      return {
        ...state,
        agents: action.payload,
      };
    case AgentAdministrationActionType.CLEAR_AGENT_MAPPING:
      return {
        ...state,
        agentMapping: DEFAULT_STATE.agentMapping,
      };
    case AgentAdministrationActionType.CHANGE_ERRORS:
      return {
        ...state,
        validationErrors: action.payload,
      };
    case AgentAdministrationActionType.CLEAR:
      return DEFAULT_STATE;
    default:
      return state;
  }
};
